import { useNavigate } from "react-router";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { CUSTOMER } from "../../../../constants/routes";
import { useEvents } from "../../../../hooks";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { usePriceTierList } from "../../../../hooks/api/v2/priceTier/usePriceTierList";
import { useBuyer } from "../../../../hooks/api/v2/retailer/useBuyer";
import { useSupplierRelationshipCreate } from "../../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipCreate";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { EditConnectionPropertiesFragment } from "../../businessConnection/connectionSelector/EditConnectionPropertiesFragment";

export const CreateConnectionContent = ({
  buyerId,
  email,
  deliveryInstructions,
}: {
  buyerId: number;
  email: string;
  deliveryInstructions?: string;
}) => {
  const { track } = useEvents();
  const connectionCreateMutation = useSupplierRelationshipCreate();

  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  if (!business) throw new Error("Cannot find business");

  const supplier = business.Supplier;

  if (!supplier) throw new Error("Cannot find Supplier");

  const buyerQuery = useBuyer(buyerId);
  const priceTiersListQuery = usePriceTierList({});

  const priceTiers = (
    priceTiersListQuery.data?.pages.flatMap((pt) => pt) ?? []
  ).filter((pt) => pt.derivationType !== "base");

  return (
    <>
      {!priceTiersListQuery.data?.pages[0]?.length ? (
        <div>You need to create a price tier before accepting connections</div>
      ) : (
        <>
          {connectionCreateMutation.isError && (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(connectionCreateMutation.error)?.response?.data
                  .message ?? "An unknown error occurred"
              }
            />
          )}
          {priceTiersListQuery.isPending ||
          connectionCreateMutation.isPending ? (
            <SpinnerLegacy />
          ) : (
            <EditConnectionPropertiesFragment
              priceTiers={priceTiers}
              buyerBusinessEmail={email}
              onSubmit={async (e) => {
                if (!e.PriceTierId) {
                  throw new Error("Price Tier not set");
                }

                if (!e.fulfillmentType) {
                  throw new Error("FulfillmentType not set");
                }

                if (!buyerId) {
                  throw new Error("Cannot find buyerId");
                }

                connectionCreateMutation.mutate(
                  {
                    SupplierId: supplier.id,
                    BuyerId: buyerId,
                    PriceTierId: parseInt(e.PriceTierId.toString()),
                    AvailabilityTierId: e.AvailabilityTierId
                      ? parseInt(e.AvailabilityTierId.toString(), 10)
                      : null,
                    OrderRulesId: e.OrderRulesId
                      ? parseInt(e.OrderRulesId.toString(), 10)
                      : null,
                    DrayDeliveryMethodId: e.DrayDeliveryMethodId
                      ? parseInt(e.DrayDeliveryMethodId.toString())
                      : null,
                    CourierDeliveryMethodId: e.CourierDeliveryMethodId
                      ? parseInt(e.CourierDeliveryMethodId.toString())
                      : null,
                    PalletDeliveryMethodId: e.PalletDeliveryMethodId
                      ? parseInt(e.PalletDeliveryMethodId.toString())
                      : null,
                    fulfillmentType: e.fulfillmentType,
                    initiator: "supplier",
                    buyerContactEmail: e.buyerContactEmail ?? null,
                    paymentDue: e.paymentDue ?? null,
                    vatPercentage:
                      e.vatPercentage || e.vatPercentage === 0
                        ? e.vatPercentage
                        : null,
                    paymentTerms: e.paymentTerms ?? null,
                    paymentDueCalculateFrom: e.paymentDueCalculateFrom ?? null,
                    notes: e.notes,
                    customerCode: e.customerCode,
                    deliveryInstructions: deliveryInstructions || null,
                  },
                  {
                    onSuccess: (response) => {
                      track("Connection Invitation Sent", {
                        initiator: "supplier",
                      });
                      navigate(CUSTOMER.href({ connectionId: response.id }));
                    },
                  },
                );
              }}
              buyerPostcode={
                buyerQuery.data?.Business.DeliveryAddress.postalCode
              }
            />
          )}
        </>
      )}
    </>
  );
};
